import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Site, page } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { useListState } from '../../lib/useList';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { ExportButton } from '../../components/ExportButton';
import { Badge, Button, Card, EmptyRow, FilterBar, PageHeader, Pager, SelectField, SortHeader, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { timeZoneNames } from './api';

const SitePage = page(Site);
const PAGE_SIZE = 20;

function NewSiteForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [town, setTown] = useState('');
  const [timeZone, setTimeZone] = useState('Europe/London');
  const create = useMutation({
    mutationFn: () => api.post('/sites', { body: { name, timeZone, address: { town } }, schema: Site }),
    onSuccess: async (site) => {
      await qc.invalidateQueries({ queryKey: ['sites'] });
      void navigate(`/manage/sites/${site.id}`);
    },
  });
  const errs = fieldErrors(create.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  return (
    <Card title={t('sitesUi.create.title')} className="mb-6">
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <ErrorLine error={create.error} />
        </div>
        <TextField label={t('sitesUi.field.name')} required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} error={errs['name']} />
        <TextField label={t('sitesUi.field.town')} maxLength={80} value={town} onChange={(e) => setTown(e.target.value)} error={errs['address']} />
        <SelectField label={t('sitesUi.field.timeZone')} value={timeZone} onChange={(e) => setTimeZone(e.target.value)} error={errs['timeZone']}>
          {timeZoneNames(timeZone).map((z) => (
            <option key={z} value={z}>
              {z}
            </option>
          ))}
        </SelectField>
        <div className="flex items-end gap-2">
          <Button type="submit" variant="primary" disabled={create.isPending || name.trim() === ''}>
            {t('sitesUi.create.submit')}
          </Button>
          <Button onClick={onDone}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

export function SitesPage() {
  const list = useListState({ sort: 'name' });
  const canManage = useCan('sites.manage');
  const [status, setStatus] = useState<'active' | 'archived' | 'all'>('active');
  const [creating, setCreating] = useState(false);
  const query = { page: list.page, pageSize: PAGE_SIZE, search: list.search, sort: list.sort, order: list.order, status };
  const q = useQuery({
    queryKey: ['sites', 'list', query],
    queryFn: () => api.get('/sites', { query, schema: SitePage }),
    placeholderData: keepPreviousData,
  });
  const newButton = canManage ? (
    <Button variant="primary" onClick={() => setCreating(true)} disabled={creating}>
      {t('sitesUi.new')}
    </Button>
  ) : null;

  return (
    <>
      <PageHeader
        title={t('sitesUi.title')}
        description={t('sitesUi.description')}
        actions={
          <>
            <ExportButton path="/sites" query={{ search: list.search, status, sort: list.sort, order: list.order }} fileName="sites" />
            {newButton}
          </>
        }
      />
      {creating ? <NewSiteForm onDone={() => setCreating(false)} /> : null}
      <FilterBar columns="sm:grid-cols-2">
        <TextField label={t('common.search')} type="search" value={list.search} onChange={(e) => list.setSearch(e.target.value)} />
        <SelectField
          label={t('common.status')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as typeof status);
            list.resetPage();
          }}
        >
          <option value="active">{t('sitesUi.filter.active')}</option>
          <option value="archived">{t('sitesUi.filter.archived')}</option>
          <option value="all">{t('sitesUi.filter.all')}</option>
        </SelectField>
      </FilterBar>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <TableWrap caption={t('sitesUi.title')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <SortHeader label={t('sitesUi.col.name')} field="name" sort={list.sort} order={list.order} onSort={list.toggleSort} />
                <Th>{t('sitesUi.col.town')}</Th>
                <Th>{t('sitesUi.col.timeZone')}</Th>
                <Th>{t('common.status')}</Th>
              </tr>
            </thead>
            <tbody>
              {q.data.items.length === 0 ? <EmptyRow cols={4} action={newButton} /> : null}
              {q.data.items.map((s) => (
                <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <Link className="font-medium text-brand hover:underline" to={`/manage/sites/${s.id}`}>
                      {s.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3">{s.address.town}</td>
                  <td className="px-4 py-3">{s.timeZone}</td>
                  <td className="px-4 py-3">
                    <Badge tone={s.status === 'active' ? 'good' : 'neutral'}>{t(`sitesUi.status.${s.status}`)}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          <Pager page={q.data.page} pageSize={q.data.pageSize} total={q.data.total} onPage={list.setPage} />
        </>
      )}
    </>
  );
}
